/**
 * Единый маппер статусов матча для всего фронта сетки.
 *
 * Backend `MatchStatus` (GoodLuck.Core/Entities/Match.cs) — 7 значений:
 * Scheduled, Ready, Pending, Live, Completed, Cancelled, Paused.
 * Раньше каждый компонент (page.tsx / match-node / viewer-view) имел свой
 * copy-paste switch, покрывавший 3–5 из них → сырые enum'ы в UI, рассогласованные
 * гейты Pause/Resume/Finish (KAN-161 #1,#3,#4,#8). Здесь — единственный источник.
 */

import { translateUi } from "@/shared/lib/ui-translate";

export type MatchStatusKind =
  | "scheduled"
  | "ready"
  | "pending"
  | "live"
  | "completed"
  | "cancelled"
  | "paused"
  | "unknown";

/**
 * Исход счёта одной стороны (KAN-650). Техническое решение и отмена — отдельные
 * состояния: их нельзя красить как сыгранную победу/поражение.
 * Неявка на бэкенде — это тоже тех. победа (`Match.isTechnicalWin`), своего флага нет.
 */
export type ScoreOutcome =
  | "winner"
  | "loser"
  | "technicalWinner"
  | "technicalLoser"
  | "cancelled"
  | "neutral";

/** Нормализует сырой статус (англ. enum, in_progress-легаси или рус. подпись) в канон. */
export function normalizeMatchStatus(raw?: string | null): MatchStatusKind {
  const s = String(raw ?? "")
    .toLowerCase()
    .trim();
  switch (s) {
    case "scheduled":
    case "запланирован":
      return "scheduled";
    case "ready":
    case "готов":
      return "ready";
    case "pending":
    case "ожидание":
      return "pending";
    case "live":
    case "in_progress":
    case "inprogress":
    case "в процессе":
      return "live";
    case "completed":
    case "завершён":
    case "завершен":
      return "completed";
    case "cancelled":
    case "canceled":
    case "отменён":
    case "отменен":
      return "cancelled";
    case "paused":
    case "пауза":
      return "paused";
    default:
      return "unknown";
  }
}

// Ключи словаря Ui — переводятся в момент вывода.
const LABEL_KEYS: Record<Exclude<MatchStatusKind, "unknown">, string> = {
  scheduled: "litMatchStatus.zaplanirovan",
  ready: "litMatchStatus.gotov",
  pending: "litMatchStatus.ozhidanie",
  live: "litMatchStatus.vProtsesse",
  completed: "litMatchStatus.zavershen",
  cancelled: "litMatchStatus.otmenen",
  paused: "litMatchStatus.pauza",
};

/** Человекочитаемая рус. подпись; для нераспознанного — очищенный сырой текст либо «Матч». */
export function matchStatusLabel(raw?: string | null): string {
  const kind = normalizeMatchStatus(raw);
  if (kind === "unknown") {
    const trimmed = String(raw ?? "").trim();
    return trimmed || translateUi("litMatchStatus.match");
  }
  return translateUi(LABEL_KEYS[kind]);
}

/** Вариант бейджа shadcn для статуса. */
export function matchStatusBadgeVariant(
  raw?: string | null,
):
  | "default"
  | "secondary"
  | "outline"
  | "destructive"
  | "info"
  | "warning"
  | "success" {
  switch (normalizeMatchStatus(raw)) {
    case "completed":
      return "success";
    case "live":
      return "default";
    case "paused":
      return "warning";
    case "cancelled":
      return "destructive";
    case "ready":
      return "info";
    case "scheduled":
    case "pending":
      return "secondary";
    default:
      return "outline";
  }
}

export const isCompletedStatus = (raw?: string | null): boolean =>
  normalizeMatchStatus(raw) === "completed";

export const isCancelledStatus = (raw?: string | null): boolean =>
  normalizeMatchStatus(raw) === "cancelled";

export const isLiveStatus = (raw?: string | null): boolean =>
  normalizeMatchStatus(raw) === "live";

export const isPausedStatus = (raw?: string | null): boolean =>
  normalizeMatchStatus(raw) === "paused";

export const isScheduledStatus = (raw?: string | null): boolean =>
  normalizeMatchStatus(raw) === "scheduled";

export function scoreOutcome(
  score: number | null | undefined,
  opponentScore: number | null | undefined,
  status?: string | null,
  isTechnicalWin?: boolean,
): ScoreOutcome {
  if (normalizeMatchStatus(status) === "cancelled") return "cancelled";
  if (
    normalizeMatchStatus(status) !== "completed" ||
    typeof score !== "number" ||
    typeof opponentScore !== "number" ||
    !Number.isFinite(score) ||
    !Number.isFinite(opponentScore) ||
    score === opponentScore
  ) {
    return "neutral";
  }

  if (isTechnicalWin) {
    return score > opponentScore ? "technicalWinner" : "technicalLoser";
  }
  return score > opponentScore ? "winner" : "loser";
}

/**
 * «Активный» матч — идёт подготовка/игра/пауза (не завершён и не отменён, при этом
 * не просто ожидает участников). Scheduled/Ready/Live/Paused. Используется для показа
 * live-борда, баннера Tauri и капитанской CTA (KAN-161 #2).
 */
export function isActiveMatchStatus(raw?: string | null): boolean {
  const kind = normalizeMatchStatus(raw);
  return (
    kind === "scheduled" ||
    kind === "ready" ||
    kind === "live" ||
    kind === "paused"
  );
}

/**
 * Матч показывает live-борд/трансляцию: Live либо Paused (пауза не должна прятать
 * борд и капитанские действия — KAN-161 #2).
 */
export function showsLiveBoardStatus(raw?: string | null): boolean {
  const kind = normalizeMatchStatus(raw);
  return kind === "live" || kind === "paused";
}

/**
 * Матч можно завершить/редактировать результат: не завершён и не отменён
 * (KAN-161 #3 — раньше исключался только completed, cancelled проходил в гейты).
 */
export function canFinishMatchStatus(raw?: string | null): boolean {
  const kind = normalizeMatchStatus(raw);
  return kind !== "completed" && kind !== "cancelled";
}
